import React, {useCallback, useMemo, useState} from 'react'
import {TitledBody} from '../common/components/TitledBody'
import {useTranslation} from 'react-i18next'
import {NumberRange, TrunkingSite} from '../app/types'
import {meSelector} from '../authentication/redux'
import {useSelector} from 'react-redux'
import {ColumnDefinition, InfiniteScrollTable} from '../common/components/table/Table'
import {Link, List, ListItem} from '@mui/material'
import {sorterHeader} from '../common/components/table/SorterHeader'
import {always, ifElse, isEmpty} from 'ramda'
import {Box, Flex} from 'rebass'
import {MoreHoriz} from '@mui/icons-material'
import ClickableTooltip from '../common/components/ClickableTooltip'
import IconButton from '@mui/material/IconButton'
import ListItemText from '@mui/material/ListItemText'
import {formatPhoneNumber} from '../common/util/formatters'
import {RouteDefinition} from '../app/App'
import {DetailTrunkingSite} from './detail'
import {hasUserPrivilege} from '../common/util/privilegeUtils'
import {FeaturePrivileges} from '../common/util/featurePrivileges'
import {useFetchData} from '../common/servercall/useFetchData'
import {api} from '../common/api'

const formatRange = (nr: NumberRange) => `${formatPhoneNumber(nr.startNumber)} - ${formatPhoneNumber(nr.stopNumber)}`

const formatRangeArray = (ranges: NumberRange[]) => ifElse(isEmpty, always(""), ranges => formatRange(ranges[0]))(ranges)

const formatAllFromRangeArray = (ranges: NumberRange[]) =>
    <List dense={true}>
        {ranges.map(rn => <ListItem><ListItemText primary={formatRange(rn)}/></ListItem>)}
    </List>

export const siteUrl = '/trunk-site/:id'

export const routes: RouteDefinition[] = [
    {
        href: siteUrl,
        component: DetailTrunkingSite,
        id: 'trunking-site',
        parentId: 'groups-trunking-overview',
        requiredPrivilege: FeaturePrivileges.trunkSite.read,
    },
].map(routeDefinition => ({ ...routeDefinition, menuId: 'groups-trunking-overview' }))

export const supportedTypes = ['SIP_OVER_INTERNET_TRUNKING','SIP_OVER_INTERNET_REGISTRATION']

export const renderNumberRanges = (ranges: NumberRange[]) =>
    <Flex justifyContent={"flex-start"} alignItems={"center"} >
        <Box ><span style={{whiteSpace: "nowrap"}}>{formatRangeArray(ranges)}</span></Box>
        {ranges.length > 1 ? <Box><ClickableTooltip title={formatAllFromRangeArray(ranges)} buttonFn={(onClick) => <IconButton size="small" color="secondary" aria-label="df" component="span" onClick={onClick}>
            <MoreHoriz/>
        </IconButton>}/></Box> : <></>}
    </Flex>

export const TrunkingSites = () => {
    const { t } = useTranslation()
    const me = useSelector(meSelector)

    // number ranges are not part of `me.trunkingSites`: they're looked up for the user's sites when this page is shown
    const fetchNumberRanges = useCallback(async (): Promise<Record<string, NumberRange[]> | undefined> => {
        const productIds = (me?.trunkingSites || []).map(site => site.productId)
        if (productIds.length === 0) {
            return {}
        }
        const {data} = await api.post<Record<string, NumberRange[]>>('/api/trunking/number-ranges', productIds)
        return data
    }, [me?.trunkingSites])
    const {data: numberRangesByProductId} = useFetchData<Record<string, NumberRange[]>>(fetchNumberRanges)

    const [currentSort, setCurrentSort] = useState<{field: keyof TrunkingSite, order: any}>({field: 'productId', order: 'asc'})

    const columns: ColumnDefinition<TrunkingSite>[] = useMemo(
        () => [
            {
                title: t('trunking-sites.table.product-id'),
                key: 'productId',
                sorter: true,
                render: (text: string, record) => {
                    const supported = !!supportedTypes.find(it => it === record.type)
                    const canNavigate = supported && hasUserPrivilege(me, FeaturePrivileges.trunkSite.read)
                    return canNavigate ? <Link href={siteUrl.replace(':id', record.productId)}>{text}</Link> : <>{text}</>
                },
            },
            {
                title: t('trunking-sites.table.name'),
                key: 'name',
                sorter: true,
                render: (text: string, record) => <>{text}</>,
            },
            {
                title: 'Alias',
                key: 'alias',
                sorter: true,
                render: (text: string, record) => <>{text}</>,
            },
            {
                title: t('trunking-sites.table.type'),
                key: 'type',
                sorter: true,
                render: (text: string, record) => <>{t(`trunking-sites.product-types.${text}`)}</>,
            },
            {
                title: t('trunking-sites.table.nr-of-channels'),
                key: 'nrOfCalls',
                sorter: true,
                render: (text: string, record) => <>{text}</>,
            },
            {
                title: t('trunking-sites.table.number-ranges'),
                key: 'numberRanges',
                sorter: false,
                render: (text: string, record) => renderNumberRanges(numberRangesByProductId?.[record.productId] ?? []),
            }
        ],
        [t, me, numberRangesByProductId],
    )

    const sort_by = (field : any, reverse : boolean, primer: ((a: string | null)  => string | undefined) | null) => {
        const key = primer ?
            function(x: any) {
                return primer(x[field])
            } :
            function(x: any) {
                return x[field]
            };

        const rev = !reverse ? 1 : -1;

        return function(a: any, b: any) {
            a = key(a); b = key(b);
            // @ts-ignore
            return rev * ((a > b) - (b > a));
        }
    }

    const sortedList = useCallback((sites: TrunkingSite[]) : TrunkingSite[] =>
        [...sites].sort(sort_by(currentSort.field, currentSort.order === 'desc',
            currentSort.field === 'nrOfCalls'? null :
                currentSort.field === 'type'? a => t(`trunking-sites.product-types.${a}`) : (a => !a ? "" :  a.toUpperCase())))
    , [currentSort, t]);

    const doSort = useCallback((sort: any) => {
        setCurrentSort(sort)
    }, [])

    const sorter = useCallback(col => sorterHeader(col, currentSort, doSort), [currentSort, doSort])

    const getRowKey = useCallback(site => site.uuid, [])

    return <>
        <TitledBody title={t('navigation.trunk-sites')}>
            <InfiniteScrollTable<TrunkingSite>
                hasNextPage={false}
                data={sortedList(me?.trunkingSites || [])}
                fetchNextPage={() => []}
                tableId={'trunkSitesTable'}
                isFetching={false}
                getRowKey={getRowKey}
                sorter={sorter}
                columns={columns}
            />
        </TitledBody>
    </>
}
